import toLower from 'lodash/toLower';
import { useIntl } from 'react-intl';

import { Breadcrumb } from '@/components/Breadcrumb';
import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import { useRouter } from '@/hooks/useRouter';
import type { StoreHit } from '@/integrations/algolia/types';
import { countryMessages } from '@/lib/boutique/helpers';
import { BASE_STORE_FINDER_URL } from '@/lib/const';
import { getTranslatedCountryName } from '@/lib/i18n';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import { messages } from '@/messages/boutique';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';

import { ReactComponent as LocationSvg } from '@/assets/icons/locationWhite.svg';

import * as S from './BoutiqueBanner.styles';

interface BoutiqueBannerProps {
  boutiqueHit: StoreHit;
}
export const BoutiqueBanner = ({ boutiqueHit }: BoutiqueBannerProps) => {
  const { formatMessage } = useIntl();
  const { locale: useRouterlocale, push, query } = useRouter();
  const languageCode = getRegionFromLocale(useRouterlocale).locale.locale;

  const { locale } = useRegionProvider();
  const langSlug = locale.slug;
  const countrySlugUpperCase = Array.isArray(query?.country)
    ? query?.country[0]?.toUpperCase()
    : (query?.country as string)?.toUpperCase();
  const translatedCountryName =
    getTranslatedCountryName(languageCode, countrySlugUpperCase) ?? '';
  const toStoresString = `${BASE_STORE_FINDER_URL}index.html?country=${countrySlugUpperCase}`;
  const getBtqlistHeadingMessage = (countrySlugUpperCase: string) => {
    const code = countrySlugUpperCase.toLowerCase();
    return (
      countryMessages[code as keyof typeof countryMessages] ??
      messages.usBtqlistHeading
    );
  };

  const nameText = boutiqueHit
    ? getTranslatedStoreValue(boutiqueHit, 'name', langSlug)
    : '';

  const handleChangeCountry = () => {
    push(
      paths.stores.breitlingBoutiques.asPath({
        country: query?.country as string,
      }),
    );
  };
  return (
    <>
      {boutiqueHit?.thumbnail_url && (
        <S.TopBannerWrapper>
          <S.BreadCrumbSection>
            <S.BreadCrumbContent>
              <Breadcrumb.Wrapper>
                <Breadcrumb
                  inverted
                  items={[
                    {
                      label: formatMessage(commonMessages.home),
                      target: paths.url(),
                    },
                    {
                      label: formatMessage(messages.boutiquesAndRetailers),
                      target: toStoresString,
                    },
                    {
                      label: translatedCountryName,
                      target: toStoresString,
                    },
                    {
                      label: formatMessage(
                        getBtqlistHeadingMessage(
                          countrySlugUpperCase.toLowerCase(),
                        ),
                      ),
                      target: paths.stores.breitlingBoutiques.asPath({
                        country: query?.country as string,
                      }),
                    },
                    {
                      // boutiqueName characters from algolia are all uppercased, to capitalize, first lowercase then capitalize
                      label: toLower(nameText),
                      target: paths.asPath(),
                    },
                  ]}
                />
              </Breadcrumb.Wrapper>
            </S.BreadCrumbContent>
          </S.BreadCrumbSection>
          <S.TextWrapper>
            <S.TextContainer>
              <S.StyledHeading data-testid='BoutiqueName'>
                {nameText}
              </S.StyledHeading>
              <S.ChangeStoreCtaWrapper>
                <S.LocationSvgWrapper>
                  <LocationSvg height={16} width={16} />
                </S.LocationSvgWrapper>
                <S.ChangeStoreCta
                  colorVariant='white'
                  data-testid='changeStoreCta'
                  isUpperCase={true}
                  label={translatedCountryName}
                  onClick={handleChangeCountry}
                  variant='link'
                >
                  <S.Chevron />
                </S.ChangeStoreCta>
              </S.ChangeStoreCtaWrapper>
            </S.TextContainer>
          </S.TextWrapper>
          <S.BannerImageWrapper>
            <S.StyledAspectRatioImage
              alt={nameText}
              aspectRatio={{ x: 4, y: 3 }}
              focalPoint={{ x: 960, y: 262 }}
              loaderProps={{ height: 240 }}
              loading='lazy'
              originalImageDimensions={undefined}
              showFallbackPlaceholder
              sizes='100vw'
              src={boutiqueHit.thumbnail_url}
              useLoader
              width='100%'
            />
          </S.BannerImageWrapper>
          <S.Mask />
        </S.TopBannerWrapper>
      )}
    </>
  );
};
